/**
 * Build the color → min-price legend for the seats diagram header.
 * Groups seats by `rgb_color` (falling back to `rgbColor`) and keeps the
 * lowest numeric `price` per group. Output is sorted ascending by price
 * so the cheapest tier renders left-most.
 *
 * Used as the data source for the "Desde $X" legend when Privileged
 * Seat is active. Replaces the legacy path that depended on the
 * trip-level `seat_price_zones[]`, which is not yet shipped by backend.
 *
 * @param {Array} input - Flat array of seats or a 2D layout. Nullish OK.
 * @returns {Array<{rgbColor: string, minPrice: number}>} Sorted entries,
 *   one per distinct rgb_color. Empty when no seat carries a color+price.
 */
export default function buildColorPriceLegend(input) {
  if (!input || !Array.isArray(input) || input.length === 0) return [];

  const flat = Array.isArray(input[0]) ? input.flat(Infinity) : input;

  const minByColor = new Map();
  flat.forEach((seat) => {
    if (!seat) return;
    const rgbColor = seat.rgb_color ?? seat.rgbColor;
    const price = Number(seat.price);
    if (!rgbColor || !Number.isFinite(price)) return;

    const current = minByColor.get(rgbColor);
    if (current === undefined || price < current) {
      minByColor.set(rgbColor, price);
    }
  });

  return Array.from(minByColor.entries())
    .map(([rgbColor, minPrice]) => ({ rgbColor, minPrice }))
    .sort((a, b) => a.minPrice - b.minPrice);
}
